import React, { useState, useRef } from 'react';
import { 
  Upload, 
  Video, 
  Smartphone, 
  Monitor, 
  Download, 
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';

interface ConversionFormat {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  aspectRatio: string;
  resolution: string;
}

const ClipConverter: React.FC = () => {
  const { currentUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string>('vertical');
  const [isConverting, setIsConverting] = useState(false);
  const [convertedUrl, setConvertedUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [conversionHistory, setConversionHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [deletingItems, setDeletingItems] = useState<Set<string>>(new Set());
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    conversionId: string;
    filename: string;
  }>({ isOpen: false, conversionId: '', filename: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [usageLimits, setUsageLimits] = useState<any>(null);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);

  const formats: ConversionFormat[] = [
    {
      id: 'vertical',
      name: 'Vertical (9:16)',
      description: 'Perfect for TikTok, Instagram Reels, YouTube Shorts',
      icon: <Smartphone className="w-5 h-5" />,
      aspectRatio: '9:16',
      resolution: '1080×1920'
    },
    {
      id: 'vertical_facecam',
      name: 'Vertical with Facecam',
      description: 'Separates facecam for vertical format',
      icon: <Video className="w-5 h-5" />,
      aspectRatio: '9:16',
      resolution: '1080×1920'
    },
    {
      id: 'original',
      name: 'Original Quality',
      description: 'Maintains original aspect ratio and quality',
      icon: <Monitor className="w-5 h-5" />,
      aspectRatio: 'Original',
      resolution: 'Original'
    }
  ];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a video file');
        return;
      }
      
      // Validate file size (100MB limit)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 100MB');
        return;
      }
      
      setSelectedFile(file);
      setConvertedUrl(null);
      setProgress(0);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
      setConvertedUrl(null);
      setProgress(0);
    } else {
      toast.error('Please drop a video file');
    }
  };

  const handleConvert = async () => {
    if (!selectedFile) {
      toast.error('Please select a video file first');
      return;
    }

    // Allow guests but with limitations
    // if (!currentUser) {
    //   toast.error('Please sign in to use the converter');
    //   return;
    // }

    setIsConverting(true);
    setProgress(0);

    // Generate a unique conversion ID
    const conversionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);

    let websocket: WebSocket | null = null;
    let progressInterval: NodeJS.Timeout | null = null;

    try {
      // Set up WebSocket for real-time progress
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/api/v1/tools/convert-clip/progress/${conversionId}`;
      
      try {
        websocket = new WebSocket(wsUrl);
        
        websocket.onopen = () => {
          // console.log('WebSocket connection established for conversion progress');
        };
        
        websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'progress' && data.progress) {
              const { percent, message: _message } = data.progress;
              setProgress(percent || 0);
              // console.log(`Conversion Progress: ${percent}% - ${message}`);
            }
          } catch (e) {
            console.error('Error parsing WebSocket message:', e);
          }
        };

        websocket.onclose = (_event) => {
          // console.log('WebSocket connection closed:', event.code, event.reason);
        };

        websocket.onerror = (error) => {
          console.warn('WebSocket error, falling back to polling:', error);
          websocket = null;
        };
        
        // Wait a moment for WebSocket to establish connection
        await new Promise(resolve => setTimeout(resolve, 100));
        
      } catch (e) {
        console.warn('WebSocket connection failed, using polling fallback:', e);
        websocket = null;
      }

      // Always start HTTP polling as backup (WebSocket might fail silently)
      progressInterval = setInterval(async () => {
        try {
          const token = currentUser ? await currentUser.getIdToken() : null;
          const progressResponse = await fetch(`/api/v1/tools/convert-clip/progress/${conversionId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          
          if (progressResponse.ok) {
            const progressData = await progressResponse.json();
            if (progressData.progress) {
              setProgress(progressData.progress.percent || 0);
              // console.log(`Polling Progress: ${progressData.progress.percent}% - ${progressData.progress.message}`);
            }
          }
        } catch (e) {
          console.error('Progress polling error:', e);
        }
      }, 2000); // Poll every 2 seconds

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('format', selectedFormat);
      formData.append('conversion_id', conversionId);

      // Upload and convert the clip
      const response = await fetch('/api/v1/tools/convert-clip', {
        method: 'POST',
        body: formData,
        headers: {
          ...(currentUser && { 'Authorization': `Bearer ${await currentUser.getIdToken()}` })
        }
      });

      // Clean up progress tracking
      if (websocket) {
        websocket.close();
        websocket = null;
      }
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Conversion failed');
      }

      // Handle response - expect JSON with download URL (like YouTube downloader)
      const result = await response.json();
      setConvertedUrl(result.download_url);
      setProgress(100);
      toast.success('Conversion completed! Click the download button below.');
      
      // Refresh usage limits after successful conversion
      fetchUsageLimits();

    } catch (error) {
      // Clean up on error
      if (websocket) {
        websocket.close();
      }
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      console.error('Conversion error:', error);
      toast.error(error instanceof Error ? error.message : 'Conversion failed');
    } finally {
      setIsConverting(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setConvertedUrl(null);
    setProgress(0);
    setIsDownloading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fetchUsageLimits = React.useCallback(async () => {
    if (isLoadingLimits) return;
    
    setIsLoadingLimits(true);
    try {
      const response = await fetch('/api/v1/tools/usage-limits', {
        headers: {
          ...(currentUser && { 'Authorization': `Bearer ${await currentUser.getIdToken()}` })
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageLimits(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage limits:', error);
    } finally {
      setIsLoadingLimits(false);
    }
  }, [currentUser, isLoadingLimits]);

  const fetchConversionHistory = React.useCallback(async () => {
    if (!currentUser || isLoadingHistory) return;
    
    setIsLoadingHistory(true);
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch('/api/v1/tools/conversion-history', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const history = await response.json();
        setConversionHistory(history.conversions || []);
      }
    } catch (error) {
      console.error('Error fetching conversion history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [currentUser, isLoadingHistory]);

  const openDeleteConfirmation = (conversionId: string, filename: string) => {
    setDeleteConfirmation({
      isOpen: true,
      conversionId,
      filename
    });
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmation({
      isOpen: false,
      conversionId: '',
      filename: ''
    });
  };

  const deleteConversionHistory = async () => {
    if (!currentUser || !deleteConfirmation.conversionId) return;
    
    const { conversionId, filename: _filename } = deleteConfirmation;
    setDeletingItems(prev => new Set(prev).add(conversionId));
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`/api/v1/tools/conversion-history/${conversionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        // Remove the item from local state
        setConversionHistory(prev => prev.filter(item => item.id !== conversionId));
        toast.success('Conversion removed from history');
      } else {
        throw new Error('Failed to delete conversion');
      }
    } catch (error) {
      console.error('Error deleting conversion:', error);
      toast.error('Failed to remove conversion from history');
    } finally {
      setDeletingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(conversionId);
        return newSet;
      });
      closeDeleteConfirmation();
    }
  };

  // Load conversion history when user is logged in (only once per user change)
  React.useEffect(() => {
    if (currentUser && !isLoadingHistory) {
      fetchConversionHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // Only depend on user ID to prevent excessive calls

  // Load usage limits on component mount (only once per user change)
  React.useEffect(() => {
    if (!isLoadingLimits) {
      fetchUsageLimits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]); // Only depend on user ID to prevent excessive calls

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold tracking-tight text-gray-900 mb-2">
            Convert Videos to Vertical Format
          </h2>
          <p className="text-primary-600">
            Transform your videos for TikTok, Instagram Reels, and YouTube Shorts
          </p>
        </div>

        <div className="space-y-6">
          {/* Guest mode banner */}
          {!currentUser && (
            <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">✨</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-red-800 font-semibold mb-1">Try ClipThis for Free!</h3>
                  <p className="text-red-700 text-sm mb-2">
                    Convert your videos to vertical format - no account required for your first try.
                  </p>
                  {usageLimits && usageLimits.user_type === 'guest' && (
                    <div className="text-red-600 text-xs mb-2">
                      <strong>Clip Conversions:</strong> {usageLimits.usage_limits.clip_conversions?.remaining || 0} remaining out of {usageLimits.usage_limits.clip_conversions?.limit || 1}
                    </div>
                  )}
                  <p className="text-red-600 text-xs">
                    Note: Free conversions include a small watermark. Create an account to remove watermarks.
                  </p>
                </div>
              </div>
            </div>
          )}



          {/* File Upload */}
          <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Video File</h3>
            
            {!selectedFile ? (
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-primary-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
              >
                <Upload className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Drop your video here or click to browse
                </p>
                <p className="text-sm text-primary-600">
                  Supports MP4, MOV, AVI, MKV files up to 100MB
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg p-4 border border-primary-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Video className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-primary-600">
                        {(selectedFile.size / (1024 * 1024)).toFixed(1)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={clearFile}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Format Selection */}
          {selectedFile && (
            <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Output Format</h3>
              <div className="space-y-3">
                {formats.map((format) => (
                  <label
                    key={format.id}
                    className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                      selectedFormat === format.id
                        ? 'border-red-500 bg-red-50'
                        : 'border-primary-200 bg-white hover:border-red-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="format"
                      value={format.id}
                      checked={selectedFormat === format.id}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                      className="sr-only"
                    />
                    <div className="flex items-center space-x-3 flex-1">
                      <div className={`p-2 rounded-lg ${
                        selectedFormat === format.id ? 'bg-red-500 text-white' : 'bg-primary-100 text-primary-600'
                      }`}>
                        {format.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{format.name}</p>
                        <p className="text-sm text-primary-600">{format.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {format.aspectRatio} • {format.resolution}
                        </p>
                      </div>
                    </div>
                    {selectedFormat === format.id && (
                      <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    )}
                  </label>
                ))}
              </div>

              {/* Convert Action - Integrated */}
              <div className="border-t border-primary-200 pt-4 mt-6">
                {!convertedUrl && !isConverting && (
                  <button
                    onClick={handleConvert}
                    disabled={!selectedFile}
                    className={`w-full py-3 px-6 rounded-lg font-medium transition-colors flex items-center justify-center ${
                      selectedFile 
                        ? 'bg-black text-white hover:bg-gray-800' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {selectedFile ? 'Convert Video' : 'Upload a video file to continue'}
                  </button>
                )}

                {isConverting && (
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-4">
                      <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
                    </div>
                    <p className="text-lg font-medium text-gray-900 mb-2">Converting Video...</p>
                    <p className="text-sm text-primary-600 mb-2">Processing your video for vertical format</p>
                    <div className="bg-primary-100 rounded-lg h-2">
                      <div
                        className={`bg-red-500 h-2 transition-all duration-300 ${progress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {convertedUrl && (
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <p className="text-lg font-medium text-gray-900">Conversion Complete!</p>
                    <p className="text-sm text-gray-600 mb-4">Click the button below to download your converted video.</p>
                    
                    <div className="space-y-2">
                      <button
                        onClick={async () => {
                          if (convertedUrl && !isDownloading) {
                            setIsDownloading(true);
                            try {
                              const urlParts = convertedUrl.split('/');
                              const filename = urlParts[urlParts.length - 1] || `converted-${selectedFormat}-${Date.now()}.mp4`;
                              
                              const response = await fetch(convertedUrl);
                              const blob = await response.blob();
                              
                              const blobUrl = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = blobUrl;
                              link.download = decodeURIComponent(filename);
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(blobUrl);
                              
                              toast.success('Download started! Check your downloads folder.');
                              
                              // Refresh conversion history for logged-in users (with small delay)
                              if (currentUser) {
                                setTimeout(() => {
                                  fetchConversionHistory();
                                }, 1000);
                              }
                              
                            } catch (error) {
                              console.error('Download error:', error);
                              // Fallback
                              const link = document.createElement('a');
                              link.href = convertedUrl;
                              link.download = '';
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              toast.success('Download link opened.');
                              
                              // Refresh conversion history for logged-in users (with small delay)
                              if (currentUser) {
                                setTimeout(() => {
                                  fetchConversionHistory();
                                }, 1000);
                              }
                            } finally {
                              setIsDownloading(false);
                            }
                          }
                        }}
                        disabled={isDownloading}
                        className="inline-flex items-center py-3 px-6 rounded-lg font-medium transition-colors w-full justify-center bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Download Converted Video
                          </>
                        )}
                      </button>
                      <button
                        onClick={clearFile}
                        className="block w-full text-primary-600 hover:text-gray-800 text-sm py-2"
                      >
                        Convert another video
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conversion History - For logged-in users */}
          {currentUser && (
            <div className="bg-primary-50/50 rounded-xl border border-primary-100 overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Video className="w-5 h-5 mr-2" />
                  Your Conversion History
                </h3>
                
                {isLoadingHistory ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-red-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Loading your conversions...</p>
                  </div>
                ) : conversionHistory.length > 0 ? (
                  <div className="space-y-3">
                    {conversionHistory.slice(0, 5).map((conversion, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-primary-200"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="relative w-12 h-8">
                            {conversion.thumbnail_url ? (
                              <>
                                <img 
                                  src={conversion.thumbnail_url} 
                                  alt="Video thumbnail"
                                  className="w-full h-full object-cover rounded border border-gray-200"
                                  onError={(e) => {
                                    // Fallback to icon if thumbnail fails to load
                                    const target = e.currentTarget as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallback = target.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.classList.remove('hidden');
                                  }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-600 rounded flex items-center justify-center hidden">
                                  <Video className="w-4 h-4 text-white" />
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-red-500 to-red-600 rounded flex items-center justify-center">
                                <Video className="w-4 h-4 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">
                              {conversion.original_filename || 'Converted Video'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {conversion.format} • {new Date(conversion.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-400">
                              {(conversion.file_size / (1024 * 1024)).toFixed(1)} MB
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={async () => {
                              try {
                                const response = await fetch(conversion.download_url);
                                const blob = await response.blob();
                                
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = conversion.filename || 'converted-video.mp4';
                                link.style.display = 'none';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                
                                toast.success('Download started!');
                              } catch (error) {
                                console.error('Download error:', error);
                                toast.error('Download failed');
                              }
                            }}
                            className="p-2 text-black hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download"
                            aria-label="Download converted video"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirmation(conversion.id, conversion.original_filename || 'Converted Video')}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete from history"
                            aria-label={deletingItems.has(conversion.id) ? "Deleting..." : "Delete from history"}
                            disabled={deletingItems.has(conversion.id)}
                          >
                            {deletingItems.has(conversion.id) ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                    {conversionHistory.length > 5 && (
                      <p className="text-xs text-gray-500 text-center pt-2">
                        Showing 5 most recent conversions
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Video className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-1">No conversions yet</p>
                    <p className="text-sm text-gray-500">Your converted videos will appear here</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tips Section */}
          <div className="bg-primary-50/50 rounded-xl p-6 border border-primary-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2 text-primary-600" />
              Conversion Tips
            </h3>
            <div className="space-y-2 text-sm text-primary-700">
              <p className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Vertical (9:16) format is perfect for TikTok, Instagram Reels, and YouTube Shorts
              </p>
              <p className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Facecam mode automatically detects and repositions face content for better viewing
              </p>
              <p className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Original format maintains your video's quality and aspect ratio
              </p>
              <p className="flex items-start">
                <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Create an account to remove watermarks and access pro features
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Conversion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{deleteConfirmation.filename}" from your conversion history? 
              This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={closeDeleteConfirmation}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteConversionHistory}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClipConverter;
